import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../../contexts/RequestsContext";
import { slotWindowOnDay } from "../../../../utils/dateUtils";
import type { NurseInShift, OpenShiftRequest, ReplaceShiftRequest } from "../../../../utils/models";
import { findGroupByKey, type GapsScopedShiftGroup } from "../gapsGroupSheets";
import { groupForLinkedRequest, unitOfRequest } from "../openShifts/gapsOpenShiftsModel";
import type { GapsHomeModel } from "../useGapsDayModel";
import { closeGapsSheetIfCurrent, GapsAssignUnitReason, type GapsSheetIntentOf, GapsSheetKind, replaceGapsSheetIfCurrent } from "../useGapsSheet";
import { GapsAddCandidatesStep } from "./GapsAddCandidatesStep";
import { GapsAddGroupPickStep } from "./GapsAddGroupPickStep";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { type GapsAddContext, resolveAddLink } from "./gapsAddContext";
import { useSheetOpening } from "./useSheetOpening";

type Target = GapsSheetIntentOf<GapsSheetKind.AddStaff>;

type Props = {
	/** The store's own intent object, or `null` when the sheet is not up (see `useSheetOpening`). */
	target: Target | null;
	home: GapsHomeModel;
	/** The day the home is showing — the day the person is added to. */
	date: DateTime;
	onPostInstead: (groupKey: string) => void;
};

/** The sheet root's test id per step: each is its own sheet to a test, while one drawer stays open. */
const SHEET_TEST_ID = { pick: "gaps-add-pick-sheet", candidates: "gaps-add-staff-sheet" } as const;

/**
 * Add someone to a shift (P3-7, P3-8), in one drawer:
 * - from a MISSING ROW, straight to the people who could fill it, pre-scoped to the row's unit, job and slot hours —
 *   and, when a posted request already stands against that hole, LINKED to it, so the add completes the request;
 * - from More → "Add to a shift", first the day's shifts (shortest-handed first), then the same list for the one picked,
 *   with no unit chosen — so the assign-unit sheet follows the add;
 * - from a posted request's "Add someone directly" (P3-10), LINKED to that request and placed where the request is
 *   drawn on the day (`groupForLinkedRequest`), on the request's own hours and in its own unit (if it names one).
 *
 * Replaces the PR 2 missing-row tap that left the home for `/add-nurse-to-shift/:date`.
 */
export const GapsAddStaffSheet: FC<Props> = ({ target, home, date, onPostInstead }) => {
	const { shown, openingKey } = useSheetOpening(target);
	// Closes THIS opening only: an add that finishes after the user moved on must not close what they moved to.
	const onClose = useCallback((): void => {
		if (shown !== null) closeGapsSheetIfCurrent(shown);
	}, [shown]);
	// The group picked on an unscoped opening, tied to that opening: a new one starts on the picker again.
	const [picked, setPicked] = useState<{ openingKey: number | null; groupKey: string | null }>({ openingKey: null, groupKey: null });
	const pickedGroupKey = picked.openingKey === openingKey ? picked.groupKey : null;
	// A linked opening goes straight to the people (it is placed on the day), as a scoped one does.
	const isOnCandidates = shown !== null && (shown.scope !== null || pickedGroupKey !== null || shown.linkedRequestId !== null);

	return (
		<GapsSheetFrame open={target !== null} onClose={onClose} dataTestId={isOnCandidates ? SHEET_TEST_ID.candidates : SHEET_TEST_ID.pick}>
			{shown !== null && openingKey !== null && (
				<GapsAddStaffFlow
					key={openingKey}
					target={shown}
					pickedGroupKey={pickedGroupKey}
					onPickGroup={(groupKey) => setPicked({ openingKey, groupKey })}
					home={home}
					date={date}
					onClose={onClose}
					onPostInstead={onPostInstead}
				/>
			)}
		</GapsSheetFrame>
	);
};

type FlowProps = Omit<Props, "target"> & {
	target: Target;
	onClose: () => void;
	/** The group chosen on the picker, for an unscoped opening; `null` while it is still on the picker. */
	pickedGroupKey: string | null;
	/** Picks a group (a key) or goes back to the picker (`null`). */
	onPickGroup: (groupKey: string | null) => void;
};

/** One opening's steps. Keyed by the opening, so a new one always starts fresh (search, selection, show-all). */
const GapsAddStaffFlow: FC<FlowProps> = ({ target, pickedGroupKey, onPickGroup, home, date, onClose, onPostInstead }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { staffRequests } = useRequestsContext();
	const { scope } = target;

	// The request the opening was linked to, as the live list has it now.
	const liveMatch = useMemo<OpenShiftRequest | ReplaceShiftRequest | undefined>(
		() => (target.linkedRequestId === null ? undefined : staffRequests.find((request) => request.id === target.linkedRequestId)),
		[target.linkedRequestId, staffRequests],
	);
	// Held once found, so the request stays described after it leaves the list (a completed add closes it) — whether it
	// is still live is `liveMatch`'s to say, not this.
	const [heldMatch, setHeldMatch] = useState<OpenShiftRequest | ReplaceShiftRequest | undefined>(liveMatch);
	if (liveMatch !== undefined && liveMatch !== heldMatch) setHeldMatch(liveMatch);
	// An open shift links; a replacement or swap the matcher tied the hole to adds unlinked; gone is gone (M7, M8).
	const {
		linkedRequestId,
		linkedRequest,
		isLost: isLinkedRequestLost,
	} = resolveAddLink({
		linkedRequestId: target.linkedRequestId,
		live: liveMatch,
		held: heldMatch,
	});

	// "Add someone directly" from a posted request's sheet (P3-10): linked, but not tapped on a hole. It is framed by the
	// group the request is drawn under on the day (its hole's, else the group of its job its hours overlap most), once the
	// day is loaded — the sheet may have been opened from Open Shifts, before this day was selected.
	const isLinkedWithoutHole = scope === null && linkedRequestId !== null;
	const linkedGroup = useMemo(
		() =>
			isLinkedWithoutHole && linkedRequest !== null && home.isDayReady
				? groupForLinkedRequest(home.dayGroups, linkedRequest, date, chosenDepartment.dayStart)
				: undefined,
		[isLinkedWithoutHole, linkedRequest, home.isDayReady, home.dayGroups, date, chosenDepartment.dayStart],
	);

	const groupKey = scope !== null ? scope.groupKey : (linkedGroup?.key ?? pickedGroupKey);
	const group: GapsScopedShiftGroup | undefined = groupKey === null ? undefined : findGroupByKey(home.groups, home.dayGroups, groupKey);

	const context = useMemo<GapsAddContext | null>(() => {
		if (group === undefined) return null;
		if (scope === null && linkedRequest !== null) {
			// No hole was tapped, so the add follows the REQUEST: its own hours and its own unit — none for a request posted
			// to any unit, and the assign-unit sheet then follows the add, as for any add without a unit. Never the unit of a
			// hole the matcher happened to place it in: the request did not ask for that unit.
			return {
				group,
				locationName: unitOfRequest(linkedRequest),
				times: { startTime: linkedRequest.message.shift_start, endTime: linkedRequest.message.shift_end },
				linkedRequest,
			};
		}
		if (scope === null) return { group, locationName: null, times: slotWindowOnDay(group, date, chosenDepartment.dayStart), linkedRequest: null };
		return {
			group,
			locationName: scope.locationName,
			// Completing a request commits the person to the request's own hours; otherwise the missing row's slot.
			times:
				linkedRequest !== null
					? { startTime: linkedRequest.message.shift_start, endTime: linkedRequest.message.shift_end }
					: slotWindowOnDay(scope, date, chosenDepartment.dayStart),
			linkedRequest,
		};
	}, [group, scope, date, chosenDepartment.dayStart, linkedRequest]);

	// A missing row whose window left the day while the sheet was up has nothing left to fill.
	useEffect(() => {
		if (scope !== null && group === undefined) onClose();
	}, [scope, group, onClose]);

	// Both hand-ons act only while this add is still the sheet up (`replaceGapsSheetIfCurrent` / `onClose`): an add that
	// finished after the user moved on leaves the person on the shift, with no unit if none was chosen, and moves nothing.
	const handleAdded = useCallback(
		(created: NurseInShift): void => {
			if (context !== null && context.locationName === null) {
				// An add with no unit (from the group picker) is placed next, with the same sheet an approve uses (P3-6).
				replaceGapsSheetIfCurrent(target, { kind: GapsSheetKind.AssignUnit, dayISO: target.dayISO, shiftId: created.id, reason: GapsAssignUnitReason.Add });
				return;
			}
			onClose();
		},
		[context, target, onClose],
	);

	if (context === null) {
		// A linked opening waits for its day rather than flash the shift picker it will not need.
		if (scope !== null || (isLinkedWithoutHole && linkedRequest !== null && !home.isDayReady)) return null;
		return (
			<GapsAddGroupPickStep
				groups={home.groups}
				date={date}
				onPick={(picked) => {
					track("gaps_add_staff_group_picked", { department_id: chosenDepartment.department_id, group: picked.key });
					onPickGroup(picked.key);
				}}
				onClose={onClose}
			/>
		);
	}

	return (
		<GapsAddCandidatesStep
			context={context}
			isLinkedRequestLost={isLinkedRequestLost}
			dayGroups={home.dayGroups}
			date={date}
			// Back to the shift picker only when the picker chose the shift; a scoped or placed opening has nowhere to go back to.
			onBack={scope === null && linkedGroup === undefined ? () => onPickGroup(null) : undefined}
			onClose={onClose}
			onAdded={handleAdded}
			onPostInstead={onPostInstead}
		/>
	);
};
